<template>
  <v-app class="portal-app">
    <SnackbarComp :SnackbarComponent="SnackbarComponent" />

    <!-- Softphone remote audio sink — must stay in the layout (never HTML hidden /
         display:none or browsers mute MediaStream playback). -->
    <audio
      id="softphone-remote-audio"
      class="softphone-remote-audio"
      autoplay
      playsinline
    ></audio>

    <div class="dashboard-ambient" aria-hidden="true">
      <div class="dashboard-orb dashboard-orb--1" />
      <div class="dashboard-orb dashboard-orb--2" />
      <div class="dashboard-orb dashboard-orb--3" />
      <div class="dashboard-grid" />
      <div class="dashboard-scanlines" />
    </div>

    <v-navigation-drawer
      v-if="!isHumanAgentUser"
      app
      v-model="Drawer"
      class="sidebar"
      :width="drawerWidth"
      :rail="sidebarRail && !$vuetify.display.smAndDown"
      :rail-width="drawerRailWidth"
      :location="$vuetify.display.smAndDown ? 'left' : undefined"
      :temporary="$vuetify.display.smAndDown"
      :permanent="!$vuetify.display.smAndDown"
    >
      <button
        v-if="!$vuetify.display.smAndDown"
        type="button"
        class="sidebar-rail-toggle"
        :aria-label="sidebarRail ? 'Expand sidebar' : 'Collapse sidebar'"
        @click="toggleSidebarRail"
      >
        <v-icon
          :icon="sidebarRail ? 'mdi-chevron-right' : 'mdi-chevron-left'"
          size="16"
        />
      </button>

      <div class="sidebar-inner">
        <div
          class="sidebar-brand"
          :class="{
            'sidebar-brand--rail': sidebarRail && !$vuetify.display.smAndDown,
            'sidebar-brand--expanded': !sidebarRail || $vuetify.display.smAndDown,
          }"
        >
          <img
            v-if="sidebarRail && !$vuetify.display.smAndDown"
            :src="brandMarkLogo"
            alt="Yandle"
            class="sidebar-brand-mark flex-shrink-0"
          />
          <template v-else>
            <img
              :src="brandFullLogo"
              alt="Yandle"
              class="sidebar-brand-logo flex-shrink-0"
            />
            <div class="brand-text min-w-0">
              <div class="brand-title text-truncate">{{ brandOrgName }}</div>
              <div
                v-if="brandOrgSipNumber"
                class="brand-subtitle text-truncate"
              >
                {{ brandOrgSipNumber }}
              </div>
            </div>
          </template>
        </div>

        <v-list nav density="compact" class="sidebar-nav">
          <v-list-item
            v-for="item in navItems"
            :key="item.name"
            :to="item.href ? undefined : { name: item.name }"
            :href="item.href ? item.href : undefined"
            :target="item.href ? '_blank' : undefined"
            :rel="item.href ? 'noopener noreferrer' : undefined"
            :prepend-icon="item.icon"
            :title="item.title"
            rounded="lg"
            class="sidebar-item"
            active-class="active-sidebar-item"
          />
        </v-list>
      </div>
    </v-navigation-drawer>

    <v-app-bar app flat :height="appBarHeight" class="app-bar" elevation="0">
      <v-app-bar-nav-icon
        v-if="!isHumanAgentUser && ($vuetify.display.smAndDown || !Drawer)"
        variant="text"
        :color="appBarIconColor"
        class="app-bar-icon-btn mr-2"
        @click.stop="Drawer = !Drawer"
      />

      <div class="app-bar-grid">
        <div class="app-bar-grid__title">
          <span class="app-bar-title">{{ pageTitle }}</span>
        </div>

        <div v-if="showAppBarSearch" class="app-bar-grid__search">
          <v-text-field
            v-model="tableSearch"
            density="comfortable"
            variant="outlined"
            hide-details
            clearable
            :placeholder="searchPlaceholder"
            prepend-inner-icon="mdi-magnify"
            class="app-bar-search"
            single-line
          />
        </div>
        <div v-else class="app-bar-grid__search" aria-hidden="true" />

        <div class="app-bar-grid__actions">
          <div
            v-if="$route.name === 'Dashboard'"
            class="app-bar-ai-status"
            title="AI call agent is operational"
          >
            <span class="app-bar-ai-status__dot" aria-hidden="true" />
            <span class="app-bar-ai-status__text">AI Active</span>
          </div>

          <v-btn
            icon
            variant="flat"
            class="app-bar-icon-btn"
            :title="isDarkTheme ? 'Switch to light theme' : 'Switch to dark theme'"
            :aria-label="isDarkTheme ? 'Switch to light theme' : 'Switch to dark theme'"
            @click="toggleTheme"
          >
            <v-icon>{{ isDarkTheme ? 'mdi-weather-sunny' : 'mdi-weather-night' }}</v-icon>
          </v-btn>


          <AppBarProfileMenu
            :display-name="displayUserName"
            :email="userEmail"
            :role-label="formatRole(userRole)"
            :org-name="brandOrgName"
            :initials="userInitials"
            @sign-out="handleSignOut"
          />
        </div>
      </div>
    </v-app-bar>

    <v-main class="main-bg">
      <v-container fluid class="main-container">
        <v-card
          rounded="xl"
          class="content-card"
          elevation="0"
          :style="contentCardStyle"
        >
          <div class="page-view">
            <router-view :key="$route.path" />
          </div>
        </v-card>
      </v-container>
    </v-main>

    <SoftphoneDialer v-if="showSoftphoneDialer" />
    <IncomingCallPopup
      v-if="isSoftphoneUser"
      :suppress-in-call-bar="showSoftphoneDialer"
    />

    <ReportIssueWidget />
  </v-app>
</template>

<script>
import { computed } from "vue";
import { useRoute } from "vue-router";
import { mapGetters, mapState } from "vuex";
import { fetchUserAttributes } from "aws-amplify/auth";
import signOut from "@/mixins/Login/signOut";
import getCurrentUserDetails from "@/mixins/Organization/getCurrentUserDetails";
import { normalizeSessionPayload } from "@/utils/session";
import { getApiMessage, isApiSuccess } from "@/mixins/Extras/ApiResponse";
import { handleAuthSessionFailure } from "@/utils/authSessionRecovery";
import { APP_BAR_HEIGHT, DRAWER_WIDTH, DRAWER_RAIL_WIDTH, SIDEBAR_RAIL_STORAGE_KEY } from "@/constants/layout";
import SnackbarComp from "@/components/Extras/SnackbarComp.vue";
import AppBarProfileMenu from "@/components/Extras/AppBarProfileMenu.vue";
import SoftphoneDialer from "@/components/Softphone/SoftphoneDialer.vue";
import IncomingCallPopup from "@/components/Softphone/IncomingCallPopup.vue";
import ReportIssueWidget from "@/components/Extras/ReportIssueWidget.vue";
import { loginBrandAssets } from "@/utils/loginBrandAssets";
import { togglePortalTheme, getStoredPortalTheme } from "@/utils/portalTheme";
import { loadOrgDetailsIntoStore } from "@/utils/orgDetailsLoader";
import { formatPhoneNumber } from "@/utils/format";
import {
  isSoftphoneEligible,
  startSoftphoneSession,
  stopSoftphoneSession,
} from "@/services/softphone";
import "@/styles/softphone/dialer.css";
const PAGE_TITLES = {
  Dashboard: "Dashboard",
  Users: "Admins / Agents",
  Teams: "Team",
  OrgDetails: "Org details",
  KnowledgeBase: "Knowledge Base",
  CallHistory: "Call history",
  Callers: "Callers",
  Transactions: "Transactions",
  ApiKeys: "API & Integrations",
  BusinessQueries: "Leads",
  Contexts: "Products / Contexts",
  Campaigns: "Campaigns / Tasks",
  CampaignDetail: "Campaigns / Tasks",
};

export default {
  name: "LandingPage",
  components: { SnackbarComp, AppBarProfileMenu, SoftphoneDialer, IncomingCallPopup, ReportIssueWidget },
  mixins: [signOut, getCurrentUserDetails],
  setup() {
    const route = useRoute();

    const contentCardStyle = computed(() => {
      const isFullBleed = ['Dashboard', 'CallHistory', 'Callers', 'KnowledgeBase', 'Transactions', 'Users', 'ApiKeys', 'BusinessQueries', 'Contexts', 'Campaigns', 'CampaignDetail', 'OrgDetails'].includes(route.name)
      const needsContainedScroll = ['CallHistory', 'Callers', 'KnowledgeBase', 'Transactions', 'Users', 'ApiKeys', 'BusinessQueries', 'Contexts', 'Campaigns', 'CampaignDetail', 'OrgDetails'].includes(route.name)
      return {
        overflow: needsContainedScroll ? 'hidden' : (isFullBleed ? 'visible' : 'hidden'),
        display: 'flex',
        flexDirection: 'column',
        flex: '1 1 auto',
        minHeight: 0,
      }
    })

    return { contentCardStyle };
  },
  data() {
    return {
      SnackbarComponent: {},
      activeTheme: getStoredPortalTheme(),
      Drawer: true,
      sidebarRail: false,
      appBarHeight: APP_BAR_HEIGHT,
      drawerRailWidth: DRAWER_RAIL_WIDTH,
      baseNavItems: [
        { name: "Dashboard", title: "Dashboard", icon: "mdi-view-dashboard" },
        { name: "KnowledgeBase", title: "Knowledge Base", icon: "mdi-robot-outline" },
        { name: "CallHistory", title: "Call history", icon: "mdi-history" },
        { name: "Callers", title: "Callers", icon: "mdi-account-voice" },
        { name: "Users", title: "Admins / Agents", icon: "mdi-account-group" },
        { name: "Teams", title: "Team", icon: "mdi-account-box-multiple-outline" },
        { name: "BusinessQueries", title: "Leads", icon: "mdi-message-text-outline" },
        { name: "Contexts", title: "Products / Contexts", icon: "mdi-bookshelf" },
        { name: "Campaigns", title: "Campaigns / Tasks", icon: "mdi-clipboard-list-outline" },
        { name: "Transactions", title: "Transactions", icon: "mdi-swap-horizontal" },
        { name: "ApiKeys", title: "API & Integrations", icon: "mdi-key-outline" },
        { name: "OrgDetails", title: "Org details", icon: "mdi-domain" },
      ],
    };
  },
  computed: {
    ...mapState([
      "userName",
      "userRole",
      "agentType",
      "currentUserId",
      "userEmail",
      "organization",
      "landingOrganization",
    ]),
    ...mapGetters(["isSessionReady"]),
    /**
     * BMS-controlled org flag gating the outbound campaign modules
     * (Products/Contexts, Leads, Campaigns/Tasks). Defaults to enabled --
     * only orgs the BMS has explicitly set outgoing_enabled: false on lose
     * these tabs, so existing orgs (which predate this field) are unaffected.
     */
    outgoingEnabled() {
      return this.organization?.outgoing_enabled !== false;
    },
    /**
     * Human agents (role AGENT, agent_type HUMAN_AGENT from
     * getCurrentUserDetails) only work their campaign queue — the drawer shows
     * just the Campaigns module for them.
     */
    isHumanAgentUser() {
      const role = String(this.userRole ?? "").trim().toUpperCase();
      if (role !== "AGENT") return false;
      const type = String(this.agentType ?? "").trim().toUpperCase();
      // AI agents never log in; treat agents without an explicit type as human.
      return type !== "AI_AGENT";
    },
    /**
     * Unlike showSoftphoneDialer (Campaign Detail only, for outbound
     * dialing), a genuine incoming call -- e.g. an on-call action redirect
     * to this agent -- can arrive on any page, so the ring/answer popup
     * needs to be eligible app-wide, not route-scoped.
     */
    isSoftphoneUser() {
      return (
        this.isSessionReady &&
        isSoftphoneEligible({ userRole: this.userRole, agentType: this.agentType })
      );
    },
    showSoftphoneDialer() {
      const routeName = this.$route.name;
      // FAB only inside an open campaign (CampaignDetail) -- never on the
      // Campaigns list, since a scratch dial from the list page has no
      // campaign/lead context to attach the call to.
      let onMyCampaignsFlow = false;
      if (routeName === "CampaignDetail") {
        const leads = String(this.$route.query.leads ?? "")
          .trim()
          .toLowerCase();
        onMyCampaignsFlow = this.isHumanAgentUser || leads === "mine";
      }
      if (!onMyCampaignsFlow) return false;
      return (
        this.isSessionReady &&
        isSoftphoneEligible({
          userRole: this.userRole,
          agentType: this.agentType,
        })
      );
    },
    navItems() {
      const OUTGOING_GATED_NAMES = ["BusinessQueries", "Contexts", "Campaigns"];
      const items = this.outgoingEnabled
        ? this.baseNavItems
        : this.baseNavItems.filter(
            (item) => !OUTGOING_GATED_NAMES.includes(item.name),
          );
      if (this.isHumanAgentUser) {
        return items.filter((item) => item.name === "Campaigns");
      }
      return items;
    },
    brandOrgName() {
      const sessionName = this.organization?.org_name?.trim();
      if (sessionName) return sessionName;
      const landingName = this.landingOrganization?.org_name?.trim();
      if (landingName) return landingName;
      return "Your organisation";
    },
    brandOrgSipNumber() {
      const sessionSipNumber = this.organization?.sip_number?.trim();
      if (sessionSipNumber) return formatPhoneNumber(sessionSipNumber);
      const landingSipNumber = this.landingOrganization?.sip_number?.trim();
      if (landingSipNumber) return formatPhoneNumber(landingSipNumber);
      return "";
    },
    brandFullLogo() {
      return this.isDarkTheme
        ? loginBrandAssets.mainLogoDark
        : loginBrandAssets.mainLogo;
    },
    brandMarkLogo() {
      return loginBrandAssets.yandleMark;
    },
    userInitials() {
      const name = this.displayUserName;
      if (!name || name === "—") return "YA";
      const parts = String(name).trim().split(/\s+/).filter(Boolean);
      if (parts.length >= 2) {
        return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
      }
      return String(name).slice(0, 2).toUpperCase();
    },
    showSidebarUser() {
      return Boolean(this.userName || this.userEmail);
    },
    displayUserName() {
      return this.userName || this.userEmail || "—";
    },
    drawerWidth() {
      if (this.$vuetify.display.smAndDown) return DRAWER_WIDTH;
      return this.sidebarRail ? DRAWER_RAIL_WIDTH : DRAWER_WIDTH;
    },
    pageTitle() {
      return PAGE_TITLES[this.$route.name] || "Yandle Portal";
    },
    showTableSearch() {
      return (
        this.$route.name === "Users" ||
        this.$route.name === "OrgDetails" ||
        this.$route.name === "CallHistory" ||
        this.$route.name === "Callers" ||
        this.$route.name === "Transactions" ||
        this.$route.name === "BusinessQueries" ||
        this.$route.name === "Contexts" ||
        this.$route.name === "Campaigns"
      );
    },
    showAppBarSearch() {
      return this.$route.name === "Dashboard" || this.showTableSearch;
    },
    tableSearch: {
      get() {
        return this.$store.state.tableSearch;
      },
      set(value) {
        this.$store.commit("setTableSearch", value ?? "");
      },
    },
    searchPlaceholder() {
      if (this.$route.name === "Dashboard") {
        return "Search calls, leads, metrics...";
      }
      if (this.$route.name === "OrgDetails") {
        return "Search organizations, contacts, phone numbers...";
      }
      if (
        this.$route.name === "CallHistory" ||
        this.$route.name === "Callers"
      ) {
        return "Search name, contact number, calls...";
      }
      if (this.$route.name === "Transactions") {
        return "Search transactions or credit requests...";
      }
      if (this.$route.name === "BusinessQueries") {
        return "Search name, email, phone, query...";
      }
      if (this.$route.name === "Contexts") {
        return "Search product name, status...";
      }
      if (this.$route.name === "Campaigns") {
        return "Search campaign name, product, status...";
      }
      return "Search team members...";
    },
    isDarkTheme() {
      return this.activeTheme === "dark";
    },
    appBarIconColor() {
      return this.isDarkTheme ? "white" : "primary";
    },
  },
  watch: {
    $route() {
      this.$store.commit("clearTableSearch");
      this.enforceAgentNavigation();
    },
    isSessionReady: {
      immediate: true,
      handler(ready) {
        if (ready) {
          this.loadOrgDetails();
          this.maybeStartSoftphone();
          this.enforceAgentNavigation();
        }
      },
    },
  },

  created() {
    this.Drawer = this.$vuetify.display.smAndDown ? false : true;
    if (typeof localStorage !== "undefined") {
      this.sidebarRail =
        localStorage.getItem(SIDEBAR_RAIL_STORAGE_KEY) === "1";
    }
    if (!this.isSessionReady) {
      this.loadSession();
    }
  },
  beforeUnmount() {
    stopSoftphoneSession();
  },
  methods: {
    /**
     * Softphone-eligible users keep one persistent SIP/WebRTC registration
     * for the whole session: HUMAN_AGENT, or OWNER/ADMIN. AI agents never
     * register. Missing extensions are self-provisioned once on start.
     */
    maybeStartSoftphone() {
      if (
        !isSoftphoneEligible({
          userRole: this.userRole,
          agentType: this.agentType,
        })
      ) {
        return;
      }
      // Need user id for self-provision if no extension exists yet.
      if (!this.currentUserId) return;
      void startSoftphoneSession(this.currentUserId);
    },
    /** Human agents can only visit the Campaigns module — bounce anything else. */
    enforceAgentNavigation() {
      if (!this.isSessionReady || !this.isHumanAgentUser) return;
      if (["Campaigns", "CampaignDetail"].includes(this.$route.name)) return;
      this.$router.replace({ name: "Campaigns" });
    },
    async loadOrgDetails() {
      if (!this.isSessionReady || this.$store.state.orgDetailsLoading) return;

      try {
        await loadOrgDetailsIntoStore(this.$store);
      } catch (err) {
        console.log("Organisation details load failed", err);
        this.showSnack(
          "warning",
          err?.message || "Could not refresh organisation details.",
        );
      }
    },
    toggleTheme() {
      this.activeTheme = togglePortalTheme(this.$vuetify);
    },
    formatRole(role) {
      if (!role) return "Team member";
      return String(role).replace(/_/g, " ");
    },
    toggleSidebarRail() {
      this.sidebarRail = !this.sidebarRail;
      if (typeof localStorage !== "undefined") {
        localStorage.setItem(
          SIDEBAR_RAIL_STORAGE_KEY,
          this.sidebarRail ? "1" : "0",
        );
      }
    },
    showSnack(color, text) {
      this.SnackbarComponent = {
        SnackbarVmodel: true,
        SnackbarColor: color,
        SnackbarText: text,
      };
    },
    async loadSession() {
      this.$store.commit("SET_SESSION_LOADING", true);
      this.$store.commit("SET_SESSION_ERROR", "");

      try {
        const attrs = await fetchUserAttributes();
        const cognitoEmail = attrs.email?.trim() || "";

        const raw = await this.getCurrentUserDetails();
        if (!isApiSuccess(raw)) {
          const message = getApiMessage(raw, "Failed to load session.");
          if (await handleAuthSessionFailure(this.$store, message, { router: this.$router })) {
            return;
          }
          throw new Error(message);
        }

        const session = normalizeSessionPayload(raw);
        if (!session?.currentUserId || !session?.orgId) {
          throw new Error("Account session is missing organisation context.");
        }

        const userEmail = session.userEmail || cognitoEmail;
        if (!userEmail) {
          throw new Error("No email found on your account.");
        }

        this.$store.commit("SET_SESSION", {
          currentUserId: session.currentUserId,
          orgId: session.orgId,
          userEmail,
          emailAddress: session.emailAddress,
          emailChannel: session.emailChannel,
          userName: session.userName,
          userRole: session.userRole,
          agentType: session.agentType,
          organization: session.organization,
        });
        this.$store.commit("SET_WORKSPACE_READY", true);
      } catch (err) {
        if (await handleAuthSessionFailure(this.$store, err, { router: this.$router })) {
          return;
        }
        const message = err.message || "Failed to load session.";
        this.$store.commit("SET_SESSION_ERROR", message);
        this.showSnack("error", message);
      } finally {
        this.$store.commit("SET_SESSION_LOADING", false);
      }
    },
    async handleSignOut() {
      stopSoftphoneSession();
      await this.signOut();
      this.$store.commit("CLEAR_SESSION");
      this.$store.commit("RESET_WORKSPACE");
      this.$store.commit("SET_AUTHENTICATED", false);
      this.$router.push({ path: "/" });
    },
  },
};
</script>

<style scoped>
/* ── Ambient (matches LoginPage) ─────────────────────── */
.dashboard-ambient {
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 0;
}

.dashboard-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: var(--y-ambient-orb-opacity);
  animation: dashboard-orb-drift 18s ease-in-out infinite;
}

.dashboard-orb--1 {
  width: 420px;
  height: 420px;
  top: -12%;
  left: -8%;
  background: radial-gradient(circle, #6366f1 0%, transparent 70%);
}

.dashboard-orb--2 {
  width: 360px;
  height: 360px;
  bottom: -10%;
  right: -6%;
  background: radial-gradient(circle, #22d3ee 0%, transparent 70%);
  animation-delay: -6s;
}

.dashboard-orb--3 {
  width: 280px;
  height: 280px;
  top: 40%;
  right: 20%;
  background: radial-gradient(circle, #8b5cf6 0%, transparent 70%);
  animation-delay: -12s;
  opacity: var(--y-login-orb-3-opacity, 0.3);
}

@keyframes dashboard-orb-drift {
  0%,
  100% {
    transform: translate(0, 0) scale(1);
  }
  33% {
    transform: translate(24px, -16px) scale(1.05);
  }
  66% {
    transform: translate(-16px, 20px) scale(0.95);
  }
}

.dashboard-grid {
  position: absolute;
  inset: 0;
  background-image: linear-gradient(
      var(--y-ambient-grid) 1px,
      transparent 1px
    ),
    linear-gradient(90deg, var(--y-ambient-grid) 1px, transparent 1px);
  background-size: 48px 48px;
  mask-image: radial-gradient(
    ellipse 80% 70% at 50% 50%,
    black 20%,
    transparent 75%
  );
  -webkit-mask-image: radial-gradient(
    ellipse 80% 70% at 50% 50%,
    black 20%,
    transparent 75%
  );
}

.dashboard-scanlines {
  position: absolute;
  inset: 0;
  background: repeating-linear-gradient(
    0deg,
    transparent,
    transparent 2px,
    var(--y-ambient-scanline) 2px,
    var(--y-ambient-scanline) 4px
  );
  opacity: 0.4;
}

.sidebar {
  position: relative;
  z-index: 2;
  color: var(--y-sidebar-text);
}

.sidebar-inner {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.sidebar-divider {
  border-color: var(--y-sidebar-divider) !important;
  opacity: 1;
}

.sidebar-nav {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
}

.sidebar-footer {
  border-top: 1px solid var(--y-sidebar-divider);
  margin-top: auto;
}

.sidebar-user {
  display: flex;
  align-items: center;
  gap: var(--y-space-3);
  border-radius: var(--y-radius-lg);
  background: var(--y-sidebar-user-bg);
  border: 1px solid var(--y-sidebar-user-border);
}

.sidebar-user-avatar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: var(--y-radius-sm);
  background: linear-gradient(135deg, #6366f1 0%, #818cf8 100%);
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.28);
}

.sidebar-user-avatar__icon {
  color: #ffffff !important;
  opacity: 1 !important;
}

.sidebar-user-meta {
  flex: 1;
  min-width: 0;
}

.sidebar-user-name {
  font-size: var(--y-font-size-sm);
  font-weight: 600;
  color: var(--y-sidebar-brand-title);
  line-height: var(--y-line-height-tight);
}

.sidebar-user-role {
  font-size: var(--y-font-size-xs);
  color: var(--y-sidebar-brand-subtitle);
  line-height: var(--y-line-height-tight);
  margin-top: 2px;
  text-transform: capitalize;
}

.brand-text {
  min-width: 0;
}

.brand-title {
  font-size: var(--y-font-size-md);
  font-weight: 700;
  color: var(--y-sidebar-brand-title);
  letter-spacing: -0.02em;
  line-height: var(--y-line-height-tight);
}

.brand-subtitle {
  font-size: var(--y-font-size-xs);
  color: var(--y-sidebar-brand-subtitle);
  margin-top: 2px;
  letter-spacing: 0.02em;
}

.sidebar-item {
  color: var(--y-sidebar-text);
  transition: background var(--y-transition-fast),
    color var(--y-transition-fast);
}

.sidebar-item :deep(.v-list-item__prepend .v-icon) {
  font-size: var(--y-sidebar-icon-size, 1.25rem);
  width: var(--y-sidebar-icon-size, 1.25rem);
  height: var(--y-sidebar-icon-size, 1.25rem);
  color: var(--y-sidebar-icon, var(--y-sidebar-text));
  opacity: 1;
  transition: color var(--y-transition-fast), transform var(--y-transition-fast);
}

.sidebar-item:hover :deep(.v-list-item__prepend .v-icon) {
  color: var(--y-sidebar-icon-hover, var(--y-sidebar-text));
}

.sidebar-item :deep(.v-list-item-title) {
  font-size: 0.8125rem;
  font-weight: 500;
  letter-spacing: 0.01em;
}

.active-sidebar-item {
  box-shadow: var(--y-sidebar-active-shadow, inset 3px 0 0 var(--y-sidebar-active-accent));
}

.active-sidebar-item :deep(.v-list-item-title),
.active-sidebar-item :deep(.v-list-item__prepend .v-icon) {
  color: var(--y-sidebar-text-active) !important;
  opacity: 1;
}

.active-sidebar-item :deep(.v-list-item__prepend .v-icon) {
  color: var(--y-sidebar-active-icon);
  opacity: 1;
}

.sidebar-brand-row {
  flex-shrink: 0;
  box-sizing: border-box;
}

.app-bar-inner {
  box-sizing: border-box;
}

.sidebar-user-email {
  font-size: var(--y-font-size-xs);
  color: var(--y-sidebar-brand-subtitle);
  line-height: var(--y-line-height-tight);
  margin-top: 2px;
}

.main-bg {
  position: relative;
  z-index: 1;
  background: transparent !important;
  min-height: 100vh;
  min-height: 100dvh;
  overflow-x: clip;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.main-bg :deep(.v-main__wrap) {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
}

.main-container {
  max-width: 100%;
  width: 100%;
  background: transparent !important;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  min-width: 0;
  height: 100%;
  padding: var(--y-space-2) var(--y-space-3) !important;
  box-sizing: border-box;
}

.content-card {
  background: var(--y-content-card-bg);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid var(--y-content-card-border);
  border-radius: var(--y-content-card-radius, var(--y-radius-xl));
  padding: var(--y-space-3) var(--y-space-4);
  box-shadow: var(--y-content-card-shadow);
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
}

.app-bar-actions {
  gap: var(--y-space-2);
}

.app-bar-search {
  width: min(100%, 380px);
}

.theme-toggle-btn--solo {
  margin-left: 0;
}

.content-card .page-view {
  flex: 1;
  min-height: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
}
</style>
